import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { stats, testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formDataObj = new FormData(form);
    const honeypot = formDataObj.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setFormStatus('submitting');
    formDataObj.delete('company_alt');

    try {
      const response = await fetch('http://98fire.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于韩漫屋 - 韩国漫画在线阅读平台',
            description: '韩漫屋汇集了众多好看的韩国漫画，形成韩漫大全。了解我们的品牌故事、平台优势和联系方式。',
            url: 'http://98fire.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫屋',
              url: 'http://98fire.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://98fire.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://98fire.com/about' },
              ],
            },
          }),
        }}
      />
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <main className="pt-24">
          <section className="py-16 md:py-24 px-6 md:px-10">
            <div className="max-w-[1280px] mx-auto">
              <nav className="flex items-center gap-2 text-xs text-stone-400 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-stone-600 transition-colors">首页</Link>
                <span>/</span>
                <span className="text-stone-600">关于我们</span>
              </nav>

              <div className="text-center max-w-2xl mx-auto mb-20">
                <h1 className="text-3xl md:text-5xl font-bold font-[family-name:var(--font-heading)] text-stone-800 leading-tight">
                  关于韩漫屋
                </h1>
                <p className="mt-6 text-stone-500 text-sm leading-relaxed">
                  韩漫屋创立于2023年，是一家专注于韩国漫画在线阅读的平台。我们致力于为华语读者打造最优质的韩漫阅读体验。
                  平台成立以来，已收录超过5000部精品韩国漫画，涵盖浪漫爱情、悬疑惊悚、古风恋爱、奇幻冒险、搞笑日常等多种类型，
                  月活跃用户超过100万，成为华语地区最受欢迎的韩漫阅读平台之一。
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-8 rounded-2xl bg-white border border-stone-100">
                    <p className="text-3xl md:text-4xl font-black text-stone-800 tracking-tight">{stat.value}</p>
                    <p className="mt-2 text-xs text-stone-400 uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                <div className="p-8 rounded-2xl bg-white border border-stone-100">
                  <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center mb-5">
                    <i className="ri-shield-check-line text-xl text-green-600"></i>
                  </div>
                  <h3 className="text-base font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-3">正版授权</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">与多家韩国漫画平台合作，所有内容均获得正版授权，为读者提供优质的阅读保障。</p>
                </div>
                <div className="p-8 rounded-2xl bg-white border border-stone-100">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-5">
                    <i className="ri-translate-2 text-xl text-amber-600"></i>
                  </div>
                  <h3 className="text-base font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-3">专业翻译</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">拥有经验丰富的韩语翻译团队，确保中文翻译准确流畅，完美呈现原作魅力。</p>
                </div>
                <div className="p-8 rounded-2xl bg-white border border-stone-100">
                  <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center mb-5">
                    <i className="ri-device-line text-xl text-rose-600"></i>
                  </div>
                  <h3 className="text-base font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-3">多端适配</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">完美适配手机、平板和电脑，随时随地畅享高清韩漫阅读体验。</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-14">
                <p className="text-[10px] font-medium text-stone-400 uppercase tracking-[0.2em] mb-3">漫迷口碑</p>
                <h2 className="text-2xl md:text-3xl font-bold font-[family-name:var(--font-heading)] text-stone-800">
                  他们都在韩漫屋
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div key={t.author} className="p-8 rounded-2xl bg-stone-50 border border-stone-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-200">
                        <img src={t.avatar} alt={t.author} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-stone-800">{t.author}</p>
                        <p className="text-xs text-stone-400">{t.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed">{t.quote}</p>
                    <div className="mt-4 flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 md:py-24 px-6 md:px-10" id="contact">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-16">
                <div className="lg:w-[45%]">
                  <h2 className="text-2xl md:text-3xl font-bold font-[family-name:var(--font-heading)] text-stone-800 mb-6">
                    联系我们
                  </h2>
                  <p className="text-sm text-stone-500 leading-relaxed mb-10">
                    有任何问题或建议？我们很乐意倾听您的声音。无论是版权合作、内容反馈还是技术支持，请随时与我们联系。
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-stone-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">电子邮箱</p>
                        <p className="text-sm text-stone-400 mt-0.5">hello@98fire.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-wechat-line text-stone-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">微信公众号</p>
                        <p className="text-sm text-stone-400 mt-0.5">韩漫屋官方</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-stone-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">工作时间</p>
                        <p className="text-sm text-stone-400 mt-0.5">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-[55%]">
                  <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-white border border-stone-100 space-y-5">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-stone-600 mb-2">姓名</label>
                      <input
                        id="about-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder:text-stone-400 outline-none focus:border-stone-300 focus:bg-white transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-stone-600 mb-2">邮箱</label>
                      <input
                        id="about-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder:text-stone-400 outline-none focus:border-stone-300 focus:bg-white transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-stone-600 mb-2">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder:text-stone-400 outline-none focus:border-stone-300 focus:bg-white transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-right text-xs text-stone-400 mt-1">{formData.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="spam-field"
                    />
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 bg-red-50 px-4 py-3 rounded-lg">{formMessage}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-8 py-3.5 bg-stone-800 text-white rounded-full text-sm font-medium hover:bg-stone-700 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-24 px-6 md:px-10 bg-white" id="faq">
            <div className="max-w-[800px] mx-auto">
              <div className="text-center mb-14">
                <p className="text-[10px] font-medium text-stone-400 uppercase tracking-[0.2em] mb-3">帮助中心</p>
                <h2 className="text-2xl md:text-3xl font-bold font-[family-name:var(--font-heading)] text-stone-800">
                  常见问题
                </h2>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="border border-stone-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      <h3 className="text-sm font-medium text-stone-800 pr-4">{faq.question}</h3>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${faqOpen === idx ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-stone-400"></i>
                      </div>
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${faqOpen === idx ? 'max-h-96 pb-5 px-6' : 'max-h-0'}`}>
                      <p className="text-sm text-stone-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}